import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';
import {
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  DateRange as DateRangeIcon,
  CreditCard as CreditCardIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

interface SubscriptionData {
  status: string;
  plan: string;
  isTrialActivated?: boolean;        // Trial aktive edildi mi?
  trialStartedAt?: any;              // Trial başlangıç tarihi
  trialEndsAt?: any;                 // Trial bitiş tarihi
  stripePlan?: string;
  currentPeriodStart?: any;
  currentPeriodEnd?: any;
  cancelAtPeriodEnd?: boolean;
  activatedAt?: any;
  paymentMethod?: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
}

interface SubscriptionCardProps {
  subscription: SubscriptionData | null;
  onManageBilling?: () => void;
  onCancelSubscription?: () => void;
  onUpgradeSubscription?: () => void;
}

export default function SubscriptionCard({
  subscription,
  onManageBilling,
  onCancelSubscription,
  onUpgradeSubscription
}: SubscriptionCardProps) {
  const [loading, setLoading] = useState(false);

  const isActive = subscription?.status === 'active';
  const isInactive = subscription?.status === 'inactive';
  const isPro = subscription?.plan === 'pro';
  const isTrial = subscription?.plan === 'trial';
  const isTrialActivated = subscription?.isTrialActivated;
  const isStripe = subscription?.paymentMethod === 'stripe';

  // Calculate days remaining for trial
  const getDaysRemaining = () => {
    if (!subscription?.trialEndsAt) return 0;
    const trialEnd = subscription.trialEndsAt.toDate ? subscription.trialEndsAt.toDate() : new Date(subscription.trialEndsAt);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Get next billing date (matches countdown logic)
  const getNextBillingDate = () => {
    // If trial is active, next billing is at trial end + paid period
    if (subscription?.trialEndsAt) {
      try {
        let trialEnd: Date;

        if (subscription.trialEndsAt.toDate) {
          trialEnd = subscription.trialEndsAt.toDate();
        } else if (typeof subscription.trialEndsAt === 'number') {
          trialEnd = new Date(subscription.trialEndsAt * 1000);
        } else {
          trialEnd = new Date(subscription.trialEndsAt);
        }

        const now = new Date();
        const trialDaysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // If trial is still active, calculate next billing after paid period
        if (trialDaysRemaining > 0) {
          const getPaidPeriodDays = (stripePlan: string) => {
            switch (stripePlan) {
              case 'monthly': return 30;
              case 'quarterly': return 90;
              case 'semiannual': return 180;
              case 'annual': return 365;
              default: return 30;
            }
          };

          const paidDays = getPaidPeriodDays(subscription.stripePlan || 'monthly');
          const nextBilling = new Date(trialEnd.getTime() + (paidDays * 24 * 60 * 60 * 1000));
          return nextBilling;
        }
      } catch (error) {
        console.error('Error calculating next billing from trial:', error);
      }
    }

    // If no trial or trial expired, use currentPeriodEnd
    if (subscription?.currentPeriodEnd) {
      try {
        return subscription.currentPeriodEnd.toDate ?
          subscription.currentPeriodEnd.toDate() :
          new Date(subscription.currentPeriodEnd);
      } catch (error) {
        console.error('Error parsing currentPeriodEnd for billing:', error);
      }
    }

    return null;
  };

  // Get combined trial + paid days remaining for Pro subscription
  const getProDaysRemaining = () => {
    console.log('DEBUG SubscriptionCard - currentPeriodEnd:', subscription?.currentPeriodEnd);
    console.log('DEBUG SubscriptionCard - trialEndsAt:', subscription?.trialEndsAt);

    const now = new Date();

    // Check if we have trial information
    if (subscription?.trialEndsAt) {
      try {
        let trialEnd: Date;

        // Handle Firebase Timestamp for trial
        if (subscription.trialEndsAt.toDate) {
          trialEnd = subscription.trialEndsAt.toDate();
        } else if (typeof subscription.trialEndsAt === 'number') {
          // Handle seconds timestamp
          trialEnd = new Date(subscription.trialEndsAt * 1000);
        } else {
          trialEnd = new Date(subscription.trialEndsAt);
        }

        console.log('DEBUG SubscriptionCard - Parsed trialEnd:', trialEnd);

        // Calculate trial days remaining
        const trialDiffTime = trialEnd.getTime() - now.getTime();
        const trialDaysRemaining = Math.ceil(trialDiffTime / (1000 * 60 * 60 * 24));

        console.log('DEBUG SubscriptionCard - Trial days remaining:', trialDaysRemaining);

        // If trial is still active, add paid period days
        if (trialDaysRemaining > 0) {
          // Get paid period length based on plan
          const getPaidPeriodDays = (stripePlan: string) => {
            switch (stripePlan) {
              case 'monthly': return 30;
              case 'quarterly': return 90;
              case 'semiannual': return 180;
              case 'annual': return 365;
              default: return 30;
            }
          };

          const paidDays = getPaidPeriodDays(subscription.stripePlan || 'monthly');
          const totalDays = trialDaysRemaining + paidDays;

          console.log('DEBUG SubscriptionCard - Trial active, paid days:', paidDays);
          console.log('DEBUG SubscriptionCard - Total combined days:', totalDays);

          return Math.max(0, totalDays);
        }
      } catch (error) {
        console.error('DEBUG SubscriptionCard - Error parsing trial date:', error);
      }
    }

    // If no trial or trial expired, use currentPeriodEnd (normal paid subscription)
    if (subscription?.currentPeriodEnd) {
      try {
        let periodEnd: Date;

        // Handle Firebase Timestamp
        if (subscription.currentPeriodEnd.toDate) {
          periodEnd = subscription.currentPeriodEnd.toDate();
        } else if (typeof subscription.currentPeriodEnd === 'number') {
          periodEnd = new Date(subscription.currentPeriodEnd * 1000);
        } else {
          periodEnd = new Date(subscription.currentPeriodEnd);
        }

        const diffTime = periodEnd.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        console.log('DEBUG SubscriptionCard - Using currentPeriodEnd, days:', diffDays);

        return Math.max(0, diffDays);
      } catch (error) {
        console.error('DEBUG SubscriptionCard - Error parsing currentPeriodEnd:', error);
      }
    }

    console.log('DEBUG SubscriptionCard - No valid period data found');
    return 0;
  };

  // Get plan display name
  const getPlanDisplayName = () => {
    if (isTrial) return 'Free Trial';
    if (isPro && subscription?.stripePlan) {
      const planNames: { [key: string]: string } = {
        monthly: 'Pro Monthly',
        quarterly: 'Pro Quarterly',
        semiannual: 'Pro Semi-Annual',
        annual: 'Pro Annual'
      };
      return planNames[subscription.stripePlan] || 'Pro Plan';
    }
    return 'Pro Plan';
  };

  // Get plan pricing
  const getPlanPricing = () => {
    if (isTrial) return '$0';
    if (subscription?.stripePlan) {
      const pricing: { [key: string]: string } = {
        monthly: '$99/month',
        quarterly: '$249/3 months',
        semiannual: '$499/6 months',
        annual: '$849/year'
      };
      return pricing[subscription.stripePlan] || '$99/month';
    }
    return '$99/month';
  };

  const daysRemaining = getDaysRemaining();
  const nextBillingDate = getNextBillingDate();

  // Handle trial not activated state - return null, will be handled by TrialActivationCard
  if (isTrial && isInactive && !isTrialActivated) {
    return null;
  }

  if (!subscription) {
    return (
      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#64748b' }}>
            No Active Subscription
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Subscribe to access premium features
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onUpgradeSubscription}
            sx={{
              background: 'linear-gradient(135deg, #2e86ab, #4a90c2)',
              fontWeight: 600,
              px: 4
            }}
          >
            View Plans
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: '16px',
        background: isPro && isActive
          ? 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
        border: isPro && isActive
          ? '2px solid #2e86ab'
          : '2px solid #f59e0b',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Status Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          left: 24,
          zIndex: 1
        }}
      >
        <Chip
          icon={isPro && isActive ? <StarIcon /> : <ScheduleIcon />}
          label={isPro && isActive ? 'PRO ACTIVE' : 'TRIAL'}
          sx={{
            background: isPro && isActive
              ? 'linear-gradient(135deg, #10b981, #059669)'
              : 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.5px'
          }}
        />
      </Box>

      <CardContent sx={{ p: 4, pt: 5 }}>
        {/* Plan Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {getPlanDisplayName()}
          </Typography>
          <Typography variant="h6" sx={{ color: '#2e86ab', fontWeight: 600 }}>
            {getPlanPricing()}
          </Typography>
        </Box>

        {/* Status Details */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {isTrial && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Trial Period
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {daysRemaining} days remaining
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, (daysRemaining / 7) * 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      background: daysRemaining > 2
                        ? 'linear-gradient(135deg, #10b981, #059669)'
                        : 'linear-gradient(135deg, #f59e0b, #d97706)',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Pro Subscription Details */}
          {isPro && isActive && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Current Period
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {getProDaysRemaining()} days until renewal
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(() => {
                    const proDaysRemaining = getProDaysRemaining();
                    const totalDays = subscription?.stripePlan === 'monthly' ? 30 :
                                     subscription?.stripePlan === 'quarterly' ? 90 :
                                     subscription?.stripePlan === 'semiannual' ? 180 :
                                     subscription?.stripePlan === 'annual' ? 365 : 30;
                    return Math.max(0, ((totalDays - proDaysRemaining) / totalDays) * 100);
                  })()}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      borderRadius: 4
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Next billing: {nextBillingDate?.toLocaleDateString() || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {isPro && nextBillingDate && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Next Billing
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {nextBillingDate.toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isPro && isActive && (
            <>
              {onManageBilling && (
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={onManageBilling}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #2e86ab, #4a90c2)',
                    fontWeight: 600,
                    flex: 1,
                    minWidth: 'fit-content'
                  }}
                >
                  Manage Billing
                </Button>
              )}

              {onCancelSubscription && !subscription?.cancelAtPeriodEnd && (
                <Tooltip title="Cancel subscription">
                  <IconButton
                    onClick={onCancelSubscription}
                    disabled={loading}
                    sx={{
                      border: '1px solid #e2e8f0',
                      '&:hover': {
                        backgroundColor: '#fef2f2',
                        borderColor: '#fecaca'
                      }
                    }}
                  >
                    <CancelIcon sx={{ color: '#ef4444' }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {isTrial && onUpgradeSubscription && (
            <Button
              variant="contained"
              startIcon={<TrendingUpIcon />}
              onClick={onUpgradeSubscription}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                fontWeight: 600,
                flex: 1
              }}
            >
              Upgrade to Pro
            </Button>
          )}

          {subscription?.cancelAtPeriodEnd && (
            <Box sx={{ width: '100%' }}>
              <Chip
                label="Subscription will be cancelled at period end"
                color="warning"
                variant="outlined"
                sx={{ width: '100%' }}
              />
            </Box>
          )}
        </Box>

        {/* Trial Warning */}
        {isTrial && isTrialActivated && isActive && daysRemaining <= 2 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              ⚠️ <strong>Trial expires {daysRemaining === 0 ? 'today' : `in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`}!</strong>
              <br />
              Upgrade to Pro to continue using all features without interruption.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}